import React from 'react';
import {Link} from 'react-router';
import {Button, Menu, Text} from '@albert-io/atomic';

import type {AdminSchool} from './useAdminSchools';

const SchoolGroup = ({heading, schools}: {heading: string; schools: AdminSchool[]}) => (
  <div>
    <Text bold size='m'>
      {heading}
    </Text>
    <div className='mt-x1 font-normal'>
      {schools.map((school) => (
        <Text as='div' size='m' key={school.id}>
          {school.name}
        </Text>
      ))}
    </div>
  </div>
);

const RestrictionSection = ({heading, schools}: {heading: string; schools: AdminSchool[]}) => (
  <section>
    <Text bold size='s' className='mb-x0-half block'>
      {heading}
    </Text>
    <ul className='m-x0 flex list-none flex-col gap-x0-quarter p-x0'>
      {schools.map((school) => (
        <li key={school.id}>
          <Text size='s'>{school.name}</Text>
        </li>
      ))}
    </ul>
  </section>
);

interface GuideRestrictionMenuProps {
  restricted: AdminSchool[];
  unrestricted: AdminSchool[];
  onEdit: () => void;
}

/** Guide-variant hover menu: per-school breakdown + an "Edit Settings" action. */
export const GuideRestrictionMenu = ({
  restricted,
  unrestricted,
  onEdit
}: GuideRestrictionMenuProps) => (
  <div role='menu' aria-label='Restriction details' className='flex flex-col text-left'>
    {restricted.length > 0 && (
      <Menu.Item readonly>
        <SchoolGroup heading='Content restricted at:' schools={restricted} />
      </Menu.Item>
    )}
    {unrestricted.length > 0 && (
      <Menu.Item readonly>
        <SchoolGroup heading='Unrestricted at:' schools={unrestricted} />
      </Menu.Item>
    )}
    <Menu.Divider />
    <Menu.Item onClick={onEdit} textProps={{color: 'brand', bold: true}}>
      Edit Settings
    </Menu.Item>
  </div>
);

interface QuestionRestrictionMenuProps {
  restricted: AdminSchool[];
  unrestricted: AdminSchool[];
  allRestricted: boolean;
  assessmentGuideHref?: string;
}

/** Question-variant hover menu: per-school breakdown + a CTA linking to the assessment guide. */
export const QuestionRestrictionMenu = ({
  restricted,
  unrestricted,
  allRestricted,
  assessmentGuideHref
}: QuestionRestrictionMenuProps) => (
  <div
    role='dialog'
    aria-label='Restriction details for this question'
    className='flex min-w-[240px] flex-col gap-x2 p-x1 text-left'
  >
    {allRestricted ? (
      <RestrictionSection heading='Restricted at all your schools:' schools={restricted} />
    ) : (
      <>
        {restricted.length > 0 && (
          <RestrictionSection heading='Restricted at:' schools={restricted} />
        )}
        {unrestricted.length > 0 && (
          <RestrictionSection heading='Unrestricted at:' schools={unrestricted} />
        )}
      </>
    )}
    {assessmentGuideHref && (
      <Button
        as={Link}
        to={assessmentGuideHref}
        variant='text'
        color='primary'
        size='s'
        className='self-start'
      >
        Edit restriction settings in the assessment guide
      </Button>
    )}
  </div>
);
